import { useGetGraphqlDataQuery } from '@repo/services';
import { DropdownRenderTypeConfig, useDebounce } from '@repo/utils';
import DataTypeSpecificInput from 'advanced-filters/components/DataTypeSpecificInput';
import { type ComparatorType } from 'advanced-filters/data/comparators';
import { DATA_TYPES_WITH_OPTIONS, type DataTypeType } from 'advanced-filters/data/dataTypes';
import { type BaseOptionsType, type FilterableEntityTypes } from 'advanced-filters/data/options';
import { getActivityAttributeOptions } from 'advanced-filters/queries/activityAttributeOptions';
import { getDealStatusOptions } from 'advanced-filters/queries/dealOptions';
import { getLeadAttributeOptions } from 'advanced-filters/queries/leadAttributeOptions';
import { getAllSalesGroups } from 'advanced-filters/queries/salesGroups';
import { getTaskOptionsBasedOnEntityType } from 'advanced-filters/queries/taskTemplate';
import { type KeyValueType, setFetchError } from 'advanced-filters/store/filter';
import { Select } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const DOCUMENT_MAP : Record<FilterableEntityTypes, {
  document: string,
  object: string,
  requireVariables?: boolean,
  skip?: boolean,
  getOptions?: (subEntity: any) => { value: string; label: string; dataType?: DataTypeType }[],
  remoteSearchable?: boolean,
}> = {
  lead_activity: {
    document: getActivityAttributeOptions,
    object: 'activityAttributeOption',
    requireVariables: true,
    remoteSearchable: true,
  },
  sales_group: {
    document: getAllSalesGroups,
    object: 'salesGroups',
  },
  lead_attributes: {
    document: getLeadAttributeOptions,
    object: 'leadAttributeOptions',
    requireVariables: true,
    remoteSearchable: true,
  },
  task_type: {
    document: '',
    object: 'taskTypeOptions',
    getOptions: getTaskOptionsBasedOnEntityType,
  },
  deal: {
    document: getDealStatusOptions,
    object: 'dealStatuses',
  },
  activity_attributes: {
    document: '',
    object: '',
    requireVariables: undefined,
    skip: undefined,
    getOptions: undefined,
    remoteSearchable: undefined,
  },
};

/**
 * Select statement which Queries options based on the entity
 * and renders the appropriate input based on the data type
 */
export default function SelectWithSubQuery({
  context,
  value,
  entity,
  subEntity,
  handleChange,
  comparisionType,
}: {
  context: 'subCondition';
  value: any;
  entity: BaseOptionsType;
  subEntity: any;
  comparisionType?: ComparatorType | undefined;
  handleChange: (value: KeyValueType) => void;
}) {
  const [dropdownSearchString, setDropdownSearchString] = useState('');
  const debouncedSearchString = useDebounce(dropdownSearchString);

  const dispatch = useDispatch();
  const {
    document,
    object,
    requireVariables,
    getOptions,
    skip: permanentSkip,
    remoteSearchable: entitySearchable,
  } = DOCUMENT_MAP[entity.entityType];

  const remoteSearchable = entitySearchable
    && subEntity?.dataTypeConfig
    && subEntity?.dataTypeConfig?.dropdownRenderType === DropdownRenderTypeConfig.REMOTE_SEARCHABLE;

  const options = getOptions?.(subEntity);
  const {
    data,
    isLoading,
    error,
  } = useGetGraphqlDataQuery({
    document,
    variables: requireVariables ? {
      id: parseInt(subEntity?.id, 10),
      searchString: remoteSearchable ? debouncedSearchString : undefined,
    } : {},
  }, {
    skip: permanentSkip || !subEntity?.id || !DATA_TYPES_WITH_OPTIONS.includes(subEntity?.dataType),
  });

  const optionsArr: any[] = [
    ...(data?.[object] || []),
    ...(options || []),
  ];

  useEffect(() => {
    if (error) {
      dispatch(setFetchError('Failed To Fetch Data'));
    }
  }, [dispatch, error]);

  if (isLoading) {
    return <Select loading />;
  }

  return (
    <DataTypeSpecificInput
      entityType={entity?.entityType as FilterableEntityTypes}
      context={context}
      value={value}
      inputDataType={subEntity?.dataType}
      handleChange={handleChange}
      options={optionsArr}
      comparisionType={comparisionType}
      dropdownProps={{
        remoteSearchable,
        searchValue: dropdownSearchString,
        onSearch: setDropdownSearchString,
      }}
    />
  );
}
